import { getSearchQuery } from "../../persist"
import { parse, makeParsedElement, ParsedElement } from "./parsing"
import { IGeneratedHTML, INormalizedNameLUT } from "../../index"

export function getCaretPosition(
    element: HTMLDivElement | null,
): number | null {
    const selection = window.getSelection()

    if (!element || !selection) {
        return null
    }

    let target = selection.anchorNode

    while (target?.parentElement && target?.parentElement?.tagName !== "DIV") {
        target = target?.parentElement || null
    }

    if (!target) {
        return null
    }

    let position = selection.anchorOffset
    for (const child of Array.from(element.childNodes)) {
        if (child !== target) {
            child.textContent && (position += child.textContent.length)
        } else {
            break
        }
    }
    return position
}

export function setCaretPosition(
    element: HTMLDivElement,
    position: number,
): void {
    let target
    for (let i = 0; i < element.childNodes.length; i++) {
        const node = element.childNodes[i]
        if (!node.textContent) {
            continue
        }
        if (position <= node.textContent.length) {
            target = node
            break
        }
        position -= node.textContent.length
    }
    if (target?.nodeType !== Node.TEXT_NODE) {
        target = target?.firstChild
    }
    if (!target) {
        return
    }

    const range = document.createRange()
    range.setStart(target, position)
    range.collapse(true)
    const selection = window.getSelection()
    selection?.removeAllRanges()
    selection?.addRange(range)
}

export function generateHTML(
    input: string,
    fieldLabels: INormalizedNameLUT,
): IGeneratedHTML {
    const parsedElements = parse(input, fieldLabels)
    let html = ""
    const length = parsedElements.length
    let selectableLength = 0
    for (let i = 0; i < length; i++) {
        const parsedElement = parsedElements[i]
        selectableLength += parsedElement.asString.length

        switch (parsedElement.type) {
            case "semicolon":
                html += '<span class="tw-mr-2">;</span>'
                continue
            case "text":
            case "colon":
                html += `<span>${escapeString(parsedElement.asString)}</span>`
                continue
            case "keyValue":
                html += `<span class="label is-dimmed">${escapeString(
                    parsedElement.key,
                )}</span>`
                html += '<span class="label is-dimmed tw-mr-1">:</span>'
                html += `<span>${escapeString(parsedElement.value)}</span>`
                continue
        }
    }

    return { html, selectableLength }
}

function escapeString(text: string | undefined): string {
    if (!text) {
        return ""
    }

    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
}

export function getInitialParsedElements(
    workspaceId: number,
    normalizedNameLUT: INormalizedNameLUT,
): ParsedElement[] {
    const previousSearchText = getSearchQuery()
    const parsedElements = parse(previousSearchText, normalizedNameLUT)

    while (parsedElements.length) {
        const inContextFieldIndex = parsedElements.findIndex(
            (parsedField) =>
                parsedField.type == "keyValue" && parsedField.key == "in",
        )
        if (inContextFieldIndex === -1) {
            break
        }
        parsedElements.splice(inContextFieldIndex, 2)
    }

    if (workspaceId !== 0) {
        parsedElements.unshift(
            makeParsedElement("in", "workspace"),
            makeParsedElement(";"),
        )
    }
    return parsedElements
}

export function getInitialHTML(
    workspaceId: number,
    normalizedNameLUT: INormalizedNameLUT,
): IGeneratedHTML {
    const parsedElement = getInitialParsedElements(
        workspaceId,
        normalizedNameLUT,
    )

    return generateHTML(
        parsedElement.map((field) => field.asString).join(""),
        normalizedNameLUT,
    )
}
